import Styles from './OperatorAlertsPage.module.css'
import Colors from 'Theme/Colors'
import { useRef, useEffect, useState, use } from 'react'
import { connect } from 'react-redux'
import LoadingBar from 'react-top-loading-bar'
import { useLocation } from 'react-router-dom'
import { IFButton, IFCount, IFFilter, IFText } from 'Components'
import { useTranslation } from 'react-i18next'
import OperatorAlertsActions from 'Stores/Alert/Actions'
import OperatorAlertsSelectors from 'Stores/Alert/Selectors'
import RequestState from 'Enums/RequestState'
import { OperatorAlertList, OperatorAlertModal } from 'Containers'
import AuthSelectors from 'Stores/Auth/Selectors'
import InfinityEnums from 'Enums/InfinityEnums'

const OperatorAlertsPage = ({
  alertCount,
  fetchAlertCount,
  fetchAlertCountRequestState,
  fetchOperatorsAlertsRequestState,
  operatorAlertFilter,
  setOperatorAlertFilter,
  fetchOperatorsAlerts,
  clearOperatorAlerts,
  operatorsAlertsPaginationOffset,
  operatorUser,
  createAlertRequestState,
  resetEditingStates,
}) => {
  const { t } = useTranslation()
  const [progress, setProgress] = useState(0)
  const [tablesHeight, setTablesHeight] = useState(0)
  const [firstLoad, setFirstLoad] = useState(true)
  const location = useLocation()
  const operatorAlertFilterRef = useRef(null)
  const operatorAlertTableRef = useRef(null)
  const operatorAlertRef = useRef()

  const canEditAlert = operatorUser.permissions.includes(
    InfinityEnums.OperatorUserPermission.CAN_EDIT_ALERT,
  )

  useEffect(() => {
    fetchAlertCount()
    fetchOperatorsAlerts(null, operatorsAlertsPaginationOffset)
  }, [])
  useEffect(() => {
    fetchAlertCount(operatorAlertFilter)
    fetchOperatorsAlerts(operatorAlertFilter, operatorsAlertsPaginationOffset)
  }, [operatorAlertFilter])

  useEffect(() => {
    const handleResize = (entries) => {
      if (entries[0]) {
        const { height } = entries[0].contentRect
        setTablesHeight(height)
      }
    }

    const resizeObserver = new ResizeObserver(handleResize)
    if (operatorAlertTableRef.current) {
      resizeObserver.observe(operatorAlertTableRef.current)
    }
    return () => {
      if (operatorAlertTableRef.current) {
        resizeObserver.unobserve(operatorAlertTableRef.current)
      }
    }
  })
  useEffect(() => {
    if (location.state?.refresh) {
      setFirstLoad(true)
      clearOperatorAlerts()
    }
  }, [location.state?.refresh])

  useEffect(() => {
    if (
      fetchOperatorsAlertsRequestState === RequestState.SUCCEEDED &&
      fetchAlertCountRequestState === RequestState.SUCCEEDED &&
      firstLoad
    ) {
      setFirstLoad(false)
    }
  }, [fetchOperatorsAlertsRequestState, fetchAlertCountRequestState])

  useEffect(() => {
    if (createAlertRequestState === RequestState.SUCCEEDED) {
      operatorAlertRef.current.dismiss()
    }
  }, [createAlertRequestState])
  useEffect(() => {
    if (
      fetchOperatorsAlertsRequestState === RequestState.LOADING &&
      progress === 0
    )
      setProgress(progress + 10)
    if (
      (fetchOperatorsAlertsRequestState !== RequestState.LOADING &&
        fetchOperatorsAlertsRequestState !== RequestState.UNINITIALIZED &&
        fetchOperatorsAlertsRequestState !== RequestState.SUCCEEDED) ||
      fetchOperatorsAlertsRequestState === RequestState.SUCCEEDED
    )
      setProgress(100)
  }, [fetchOperatorsAlertsRequestState])

  const handleFilterChange = (newFilter) => {
    clearOperatorAlerts()
    setOperatorAlertFilter(newFilter)
  }

  return (
    <div className={Styles.Wrapper}>
      <LoadingBar
        color={Colors.primary}
        progress={progress}
        onLoaderFinished={() => setProgress(0)}
      />
      <div
        className={Styles.AlertsContainer}
        style={{
          backgroundColor: Colors.white,
          borderColor: Colors.DividerColor,
        }}
      >
        <div className={Styles.TitleContainer}>
          <div className={Styles.TitleDiv}>
            <IFText
              className={Styles.TitleText}
              loadSkeleton={
                fetchOperatorsAlertsRequestState === RequestState.LOADING &&
                firstLoad
              }
              skeletonWidth={'5rem'}
            >
              {t('OperatorAlertsPage.Title')}
            </IFText>
            <IFCount
              count={alertCount}
              isLoading={fetchAlertCountRequestState === RequestState.LOADING}
            />
          </div>
          <div>
            {canEditAlert && (
              <IFButton
                size="xsm"
                color={Colors.primary}
                isFill={false}
                isLoading={false}
                text={t('OperatorAlertsPage.Add')}
                className={Styles.CreateButton}
                onClick={() => operatorAlertRef.current.show()}
                loadSkeleton={
                  fetchOperatorsAlertsRequestState === RequestState.LOADING &&
                  firstLoad
                }
              />
            )}
          </div>
        </div>

        <div className={Styles.OperatorAlertFilterDiv}>
          <IFFilter
            ref={operatorAlertFilterRef}
            onFilterChange={(newFilter) => handleFilterChange(newFilter)}
            filters={operatorAlertFilter}
            textFieldPlaceholder={t('OperatorAlertsPage.FilterPlaceholder')}
            isLoading={
              firstLoad &&
              fetchOperatorsAlertsRequestState === RequestState.LOADING
            }
          />
        </div>
        <div className={Styles.Tables} ref={operatorAlertTableRef}>
          <OperatorAlertList tablesHeight={tablesHeight} />
        </div>
      </div>
      <OperatorAlertModal
        ref={operatorAlertRef}
        isNew={true}
        onDismiss={() => resetEditingStates()}
      />
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchOperatorsAlerts: (filter, offset) =>
      dispatch(OperatorAlertsActions.fetchOperatorsAlerts(filter, offset)),
    fetchAlertCount: (filter) =>
      dispatch(OperatorAlertsActions.fetchAlertCount(filter)),
    setOperatorAlertFilter: (filter) =>
      dispatch(OperatorAlertsActions.setOperatorAlertFilter(filter)),
    clearOperatorAlerts: () =>
      dispatch(OperatorAlertsActions.clearOperatorAlerts()),
    resetEditingStates: () =>
      dispatch(OperatorAlertsActions.resetEditingStates()),
  }
}

const mapStateToProps = (state) => ({
  alertCount: OperatorAlertsSelectors.getAlertCount(state),
  fetchAlertCountRequestState:
    OperatorAlertsSelectors.getFetchAlertCountRequestState(state),
  operatorAlertFilter: OperatorAlertsSelectors.getOperatorAlertFilter(state),
  fetchOperatorsAlertsRequestState:
    OperatorAlertsSelectors.getFetchOperatorsAlertsRequestState(state),
  operatorsAlertsPaginationOffset:
    OperatorAlertsSelectors.getOperatorsAlertsPaginationOffset(state),
  operatorUser: AuthSelectors.getOperatorUser(state),
  createAlertRequestState:
    OperatorAlertsSelectors.getCreateAlertRequestState(state),
  editAlertRequestState:
    OperatorAlertsSelectors.getEditAlertRequestState(state),
})

OperatorAlertsPage.propTypes = {}

export default connect(mapStateToProps, mapDispatchToProps)(OperatorAlertsPage)
